<template>
  <div class="image-editor">
    <div class="description-wrap py-4 px-4 d-flex" style="border-bottom: 1px solid #444">
      <v-btn class="mr-2 mt-2" v-if="prevRoute?.name == 'dashboard'" small icon :to="{ name: 'issues' }"
        ><v-icon class="mr-1">mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn class="mr-2 mt-2" small icon v-else-if="prevRoute && prevRoute.name" :to="{ name: prevRoute.name }"
        ><v-icon class="mr-1">mdi-chevron-left</v-icon>
      </v-btn>
      <div class="flex-grow-1">
        <v-textarea
          style="margin-left: -6px"
          hide-details
          dense
          filled
          outlined
          v-model="issue.description"
          auto-grow
          rows="1"
          row-height="15"
          class="textarea"
          :label="$t('t_logit_desc')"
          @blur="updateIssue"
          :disabled="!isOwner && !imagePhotoProp?.length"
        >
          <template v-slot:prepend v-if="$vuetify.breakpoint.mdAndUp">
            <v-avatar width="40" height="28" tile style="position: relative; max-height: 28px">
              <img :src="require('@/assets/log-it-logo-opacity.png')" />
            </v-avatar>
          </template>
        </v-textarea>

        <div v-if="issue.tags && issue.tags.length" :class="[isMobile ? 'ml-1' : 'ml-6 pl-8', 'mt-2']">
          <tag-chips :tags="issue.tags" :isApplied="true" :displayCount="isMobile ? 2 : 8" />
        </div>
      </div>

      <v-btn
        class="ml-2"
        style="margin-top: 2px"
        color="primary"
        v-if="isOwner || imagePhotoProp?.length"
        @click="submitIssue"
        >{{ $t("t_submit") }}</v-btn
      >
      <v-btn
        v-if="!$vuetify.breakpoint.xsOnly && (isOwner || imagePhotoProp?.length)"
        class="ml-2"
        style="margin-top: 2px"
        color="primary"
        outlined
        @click="shareLogit"
      >
        <v-icon class="mr-2">mdi-share-variant</v-icon>
        {{ $t("t_share") }}
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            style="margin: 6px 0 0 0px; position: relative; right: -8px"
            icon
            small
            @click="asset = null"
            color="secondary"
            v-bind="attrs"
            v-on="on"
            ><v-icon>mdi-dots-vertical</v-icon></v-btn
          >
        </template>

        <v-list v-show="isOwner || imagePhotoProp?.length">
          <v-list-item v-if="$vuetify.breakpoint.xsOnly" link @click="shareLogit">
            <v-icon class="mr-2">mdi-share-variant</v-icon>
            <v-list-item-title>{{ $t("t_share") }}</v-list-item-title>
          </v-list-item>
          <v-list-item link @click="deleteIssueConfirm">
            <v-icon class="mr-2">mdi-delete</v-icon>
            <v-list-item-title>{{ $t("t_delete") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-card flat class="image-manager ma-4 pa-0">
      <v-card-text class="d-flex align-center pa-0 image-manager-text">
        <div
          v-if="isOwner || imagePhotoProp?.length"
          @click="addImages()"
          class="thumb add-thumb d-flex flex-column align-center justify-center ma-3"
        >
          <v-icon color="#1976d2">mdi-plus</v-icon>
          <span>{{ $t("t_add") }}</span>
        </div>

        <div class="scroll-row d-flex align-center">
          <div
            v-for="(img, idx) in images"
            :key="idx"
            class="thumb ma-3"
            :class="{ active: currentImageIndex === idx }"
            @click="!showCheckboxes && updateImage(img, idx)"
          >
            <v-img :src="img?.imageSrc" class="thumb-img"></v-img>

            <v-checkbox
              v-if="showCheckboxes"
              v-model="selectedImages"
              :value="img.id"
              class="thumb-checkbox fade-in"
              hide-details
              density="compact"
              :ripple="false"
            >
              <template #label></template>
            </v-checkbox>

            <div class="thumb-number">
              {{ idx + 1 }}
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="d-flex justify-space-between align-center flex-wrap">
        <div class="d-flex align-center mb-2 ml-2 mb-sm-0">
          <v-icon small color="grey lighten-1" class="mr-1">mdi-camera</v-icon>
          <span> {{ $t("t_photo") }} {{ currentImageIndex + 1 }} {{ $t("t_of") }} {{ images.length }} </span>

          <v-btn v-if="images.length > 2" text small class="ml-3" @click="toggleMultiSelect">
            <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>
            {{ showCheckboxes ? $t("t_cancel") : $t("t_delete_multiple") }}
          </v-btn>
        </div>

        <v-btn
          v-if="isOwner || imagePhotoProp?.length"
          text
          small
          class="remove-btn"
          @click="deleteIssueImageConfirm()"
        >
          ✕ {{ $t("t_remove_selected") }}
          <span v-if="showCheckboxes && selectedImages.length">({{ selectedImages.length }})</span>
        </v-btn>
      </v-card-actions>
    </v-card>

    <div class="mx-4 py-2 pb-3">
      <v-row no-gutters>
        <!-- Toolbar -->
        <v-col cols="12" md="5">
          <div class="editor-toolbar">
            <v-btn
              v-for="btn in toolbarButtons"
              :key="btn.mode"
              :disabled="!issueId || loading || !isOwner"
              @click="mode = btn.mode"
              class="btn-editor mr-2 mt-1"
              :class="{ active: mode === btn.mode }"
            >
              <v-icon class="mr-2">{{ btn.icon }}</v-icon>
              {{ $t(btn.label) }}
            </v-btn>
          </div>
        </v-col>

        <!-- Drawing Tools -->
        <v-col v-if="mode === 'draw'" cols="12" md="7" :class="$vuetify.breakpoint.mdAndUp ? 'text-right' : ''">
          <div :class="$vuetify.breakpoint.smAndDown ? 'mt-1' : 'mt-0'">
            <!-- Pen, Marker, Eraser -->
            <v-btn
              v-for="toolBtn in toolButtons"
              :key="toolBtn.tool"
              class="mt-1 btn-tool"
              @click="tool = toolBtn.tool"
              :x-small="$vuetify.breakpoint.xsOnly"
              :class="{ active: tool === toolBtn.tool, 'ml-2': toolBtn.ml }"
            >
              <v-icon class="mr-1" small>{{ toolBtn.icon }}</v-icon>
              {{ $t(toolBtn.label) }}
            </v-btn>

            <!-- Shapes Menu -->
            <v-menu
              v-model="showShapesMenu"
              offset-y
              style="z-index: 101"
              ref="shape-picker"
              close-on-content-click
              transition="slide-x-reverse-transition"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      v-bind="{ ...attrs, ...tooltipAttrs }"
                      v-on="{ ...on, ...tooltipOn }"
                      class="btn-tool mt-1 ml-2"
                      icon
                      :x-small="$vuetify.breakpoint.xsOnly"
                      :class="{ active: isShapeToolActive() }"
                    >
                      <v-icon>mdi-shape-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("t_photo") }}</span>
                </v-tooltip>
              </template>
              <v-list dense>
                <v-list-item v-for="shape in shapeOptions" :key="shape.value" @click="selectShapeTool(shape.value)">
                  <v-list-item-icon class="mr-1">
                    <v-icon small>{{ shape.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t(shape.label) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-tooltip bottom z-index="100">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="mt-1 ml-2 btn-tool"
                  @click="undo"
                  :x-small="$vuetify.breakpoint.xsOnly"
                >
                  <v-icon>mdi-undo</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("t_undo") }}</span>
            </v-tooltip>
            <v-tooltip bottom z-index="100">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="mt-1 ml-2 btn-tool"
                  @click="redo"
                  :x-small="$vuetify.breakpoint.xsOnly"
                >
                  <v-icon>mdi-redo</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("t_redo") }}</span>
            </v-tooltip>

            <!-- Textbox -->
            <v-tooltip bottom z-index="100">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-2 mt-1 btn-tool"
                  @click="tool = 'textbox'"
                  icon
                  :x-small="$vuetify.breakpoint.xsOnly"
                  :class="{ active: tool === 'textbox' }"
                >
                  <v-icon>mdi-format-text-variant-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("t_textbox") }}</span>
            </v-tooltip>

            <!-- Color Picker -->
            <v-menu
              style="z-index: 101"
              ref="menu-color-picker"
              v-model="showColorPicker"
              :close-on-content-click="false"
              transition="slide-x-reverse-transition"
              min-width="auto"
              :nudge-left="256"
              :nudge-bottom="44"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="btn-tool mt-1 ml-2"
                  icon
                  :x-small="$vuetify.breakpoint.xsOnly"
                  :class="{ active: tool === 'color-select' }"
                >
                  <v-icon :color="pickerColor">mdi-circle-slice-8</v-icon>
                </v-btn>
              </template>
              <v-color-picker
                v-if="showColorPicker"
                class="color-picker"
                dot-size="25"
                swatches-max-height="200"
                mode="hexa"
                v-model="pickerColor"
              />
            </v-menu>

            <!-- Save Button -->
            <v-btn class="btn-submit mt-1 ml-2" color="primary" @click="saveMarkup" :small="$vuetify.breakpoint.xsOnly">
              {{ $t("t_save") }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <div
      class="canvas-wrap"
      id="canvasWrap"
      ref="canvasWrap"
      :class="mode + ' ' + tool"
      :width="canvasWidth"
      :height="canvasHeight"
    >
      <canvas class="canvas fabric" ref="canvasFabric"></canvas>
      <img id="photo" class="photo" :src="selectedImage?.imageSrc" :width="canvasWidth" />
      <div
        @click.self="addComment"
        ref="filteredComments"
        class="comments-wrap"
        :style="{
          width: canvasWidth + 'px',
          height: $vuetify.breakpoint.smAndDown ? 'auto' : canvasHeight + 'px',
          zIndex: mode == 'comment' ? 30 : 1,
        }"
      >
        <div v-for="(comment, index) in filteredComments" :key="index">
          <div
            class="comment-wrap"
            :class="currentComment?.index == index ? 'active' : ''"
            :style="{
              top: comment.yCoord + '%',
              left: comment.xCoord + '%',
              zIndex: comment.showDetails ? 34 : 1,
            }"
          >
            <div class="comment" :id="'comment-' + index">
              <div
                class="trigger-show-comment"
                :id="'triggerToggleComment_' + index"
                :class="{ 'show-move': comment.showDetails }"
                @mousedown="triggerComment($event, comment)"
                @touchstart="triggerComment($event, comment)"
                @mouseup="toggleComment(comment.index)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50">
                  <circle
                    class="large-circle"
                    cx="25"
                    cy="25"
                    r="18"
                    :fill="isNotOwner(comment) ? '#f9eaad' : '#fbecec'"
                  />
                  <circle cx="25" cy="25" r="12" :fill="commentColor(comment.severity)" />
                  <text x="25" y="30" font-family="Arial" font-size="16" text-anchor="middle" fill="white">
                    {{ index + 1 }}
                  </text>
                </svg>

                <div
                  class="edit-connect"
                  v-if="comment.showDetails"
                  :class="{ 'comment-above': comment.showAbove }"
                ></div>
              </div>

              <div
                :style="{
                  left: `calc(${-comment.xCoord}% + 25px)`,
                  top: comment.showAbove ? '-280px' : '15px',
                  width: canvasWidth - 30 + 'px',
                }"
                :id="'editComment_' + index"
                class="edit-comment"
                :class="{
                  dark: $vuetify.theme.dark,
                  'comment-above': comment.showAbove,
                  'comment-left': comment.xCoord > 70,
                }"
                v-if="comment.showDetails"
              >
                <v-btn class="btn-close-comment" @click="hideComment(comment.index)" icon x-small
                  ><v-icon color="grey darken-2" small>mdi-close-thick</v-icon></v-btn
                >
                <div class="d-flex align-center">
                  <span class="mr-2 mt-0 pt-0 pl-1 overline" style="font-size: 14px !important"
                    >{{ $t("t_logit") }} {{ $t("t_comment") }}</span
                  >
                  <v-menu offset-y v-model="info1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon>
                        <v-icon color="grey">mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title class="pb-0"
                        >{{ $t("t_logit") }} {{ $t("t_comment") }} {{ $t("t_severity") }}</v-card-title
                      >
                      <v-card-text class="pb-0">
                        <strong>{{ $t("t_store") }}-it</strong>: {{ $t("t_store_info") }}<br />
                        <strong>{{ $t("t_review") }}-it</strong>: {{ $t("t_review_info") }}<br />
                        <strong>{{ $t("t_escalate") }}-it</strong>: {{ $t("t_escalate_info") }}
                      </v-card-text>
                      <v-card-actions>
                        <v-btn text color="primary" @click="info1 = false">{{ $t("t_close") }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                  <v-spacer></v-spacer>
                  <v-icon style="position: relative; right: 0; top: -4px" large :color="commentColor(comment.severity)">
                    mdi-fire
                  </v-icon>
                </div>

                <div class="comment-read pa-2" v-if="isNotOwner(comment)">
                  {{ comment.comment }}
                </div>
                <v-textarea
                  v-else
                  dense
                  filled
                  auto-grow
                  rows="1"
                  row-height="15"
                  hide-details="true"
                  class="textarea"
                  v-model="comment.comment"
                ></v-textarea>

                <div class="text-right mt-4" v-if="isNotOwner(comment)">
                  <app-user-avatar
                    class="comment-avatar"
                    :user="{
                      profileImageAssetId: comment.owner?.profileImageAssetId,
                      profileImageAssetKey: comment.owner?.profileImageAssetKey,
                      initials: comment.owner?.initials,
                      userId: comment.owner?.id,
                    }"
                    :size="28"
                  />
                  <span class="mt-2 ml-2">{{ comment.ownerFriendlyName }}</span>
                </div>
                <v-slider
                  class="mt-2"
                  v-model="comment.severity"
                  :tick-labels="ticksLabels"
                  :color="commentColor(comment.severity)"
                  min="1"
                  max="3"
                  step="1"
                  ticks="always"
                  tick-size="4"
                  :disabled="isNotOwner(comment)"
                >
                </v-slider>
                <span class="d-flex" v-if="!isNotOwner(comment)">
                  <v-btn color="primary" small class="mt-2" @click="submitComment(comment, true)">{{
                    $t("t_submit")
                  }}</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    @click="
                      comment.id ? removeCommentConfirm(comment.index, $event) : removeComment(comment.index, $event)
                    "
                    x-small
                    class="mt-2"
                    >{{ $t("t_delete") }}</v-btn
                  >
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AppConfirmDialog ref="confirm" />
    <share-dialog v-model="shareDialog" :users="users" @share="handleShare"></share-dialog>
  </div>
</template>

<script>
import { AssetService, IssueService, UserService } from "@/services";
import { mapGetters, mapActions, mapState } from "vuex";
import AppConfirmDialog from "@/components/AppConfirmDialog.vue";
import AppUserAvatar from "@/components/AppUserAvatar.vue";
import { fabric } from "fabric-with-erasing";
import helpers from "@/mixins/helpers";
import idb from "@/api/idb";
import { ShareDialog } from "@ranes-inc/shared-lib";
import { Camera } from "@capacitor/camera";
import heic2any from "heic2any";
import { shapeOptions } from "@/constants/shapeOptions";
import { TagChips } from "@ranes-inc/shared-lib";

export default {
  name: "IssueEditor",
  mixins: [helpers],
  data() {
    return {
      isDrawing: false,
      isDragging: false,
      canvasWidth: 800,
      canvasHeight: 600,
      canvasFabric: null,
      mode: "",
      tool: "pen",
      comments: [],
      offset: { xCoord: 0, yCoord: 0 },
      currentComment: null,
      pickerColor: "#FF0000",
      showColorPicker: false,
      issue: { description: "", ownerId: null },
      issueId: null,
      selectedImage: null,
      originalWidth: null,
      originalHeight: null,
      drawSavePending: false,
      info1: false,
      shareDialog: false,
      users: [],
      currentImageIndex: -1,
      images: [],
      filteredComments: [],
      showShapesMenu: false,
      shapeRef: null,
      shapeOptions,
      showCheckboxes: false,
      selectedImages: [],
      toolbarButtons: [
        { mode: "comment", icon: "mdi-comment-text", label: "t_comment" },
        { mode: "draw", icon: "mdi-draw", label: "t_draw" },
      ],
      toolButtons: [
        { tool: "pen", icon: "mdi-pen", label: "t_pen" },
        { tool: "marker", icon: "mdi-marker", label: "t_highlight", ml: true },
        { tool: "eraser", icon: "mdi-eraser", label: "t_erase", ml: true },
      ],
      undoStack: [],
      redoStack: [],
    };
  },
  components: {
    AppConfirmDialog,
    AppUserAvatar,
    ShareDialog,
    TagChips,
  },
  props: {
    imagePhotoProp: {
      type: Array,
      default: () => [],
    },
    issueProp: {
      type: Object,
      default: null,
    },
    prevRoute: {
      type: Object,
      default: null,
    },
  },
  watch: {
    mode(mode) {
      if (!this.canvasFabric) return;
      this.canvasFabric.discardActiveObject();
      this.canvasFabric.requestRenderAll();
      this.$refs.filteredComments.style.zIndex = 1;
      this.$refs.canvasFabric.parentElement.style.zIndex = 1;
      this.canvasFabric.isDrawingMode = false;
      if (mode === "comment") {
        this.$refs.filteredComments.style.zIndex = 30;
      } else if (mode === "draw") {
        this.$refs.canvasFabric.parentElement.style.zIndex = 30;
        this.canvasFabric.isDrawingMode = true;
      }
    },
    isOwner() {
      if (this.isOwner) this.mode = "comment";
    },
    async selectedImage() {
      this.undoStack = [];
      this.redoStack = [];
      if (this.canvasFabric) this.canvasFabric.clear();
      const matched = this.issue?.issueImages?.find((img) => img.imageAssetId === this.selectedImage?.id);
      if (!matched) return [];
      //update comments for the selected image
      this.filteredComments = this.comments.filter((c) => c.issueImageId === matched.issueImageId);
      await this.getCanvasJSON();
      if (this.canvasFabric) this.saveCanvasState();
    },

    comments: {
      handler() {
        const matched = this.issue?.issueImages?.find((img) => img.imageAssetId === this.selectedImage?.id);
        if (!matched) {
          this.filteredComments = [];
          return;
        }
        //update comments for the selected image
        this.filteredComments = this.comments.filter((c) => c.issueImageId === matched.issueImageId);
      },
      deep: true,
    },

    pickerColor() {
      const pencilBrush = new fabric.PencilBrush(this.canvasFabric);
      pencilBrush.color = this.pickerColor;
      this.setTool();
    },

    tool() {
      this.setTool();
    },
  },
  computed: {
    ...mapGetters("user", ["user"]),
    ...mapState("loader", ["loading"]),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    penWidth() {
      const baseWidth = this.canvasWidth * 0.005;
      const minWidth = 6;
      const maxWidth = 11;
      return Math.min(Math.max(baseWidth + 4, minWidth), maxWidth);
    },
    ticksLabels() {
      return [this.$t("t_store") + "-it", this.$t("t_review") + "-it", this.$t("t_escalate") + "-it"];
    },
    currentUserId() {
      return this.user?.userId ? Number(this.user.userId) : null;
    },
    isOwner() {
      if (!this.issue) return false;
      return this.issue.ownerId === this.currentUserId;
    },
  },
  methods: {
    ...mapActions("loader", ["pending", "done"]),
    ...mapActions("issue", ["setImage"]),
    setTool() {
      if (!this.canvasFabric) return;

      this.resetCanvasState();

      switch (this.tool) {
        case "textbox":
          this.setupTextboxTool();
          break;

        case "pen":
          this.setupPenTool();
          break;

        case "marker":
          this.setupMarkerTool();
          break;

        case "eraser":
          this.setupEraserTool();
          break;

        case "rectangle":
        case "circle":
        case "triangle":
        case "ellipse":
        case "line":
        case "arrow":
        case "doubleArrow":
          this.setupShapeTool();
          break;

        default:
          this.canvasFabric.isDrawingMode = false;
      }
    },
    // Clear event listeners and reset canvas state
    resetCanvasState() {
      const c = this.canvasFabric;
      if (c) {
        c.discardActiveObject();
        c.requestRenderAll();
        c.off("mouse:down");
        c.off("mouse:move");
        c.off("mouse:up");
        c.isDrawingMode = false;
      }
    },
    // Textbox tool setup
    setupTextboxTool() {
      const fabric = window.fabric;
      const c = this.canvasFabric;
      c.isDrawingMode = false;

      // Remove any previous listeners to avoid duplicates
      c.off("mouse:down");
      c.off("text:editing:entered");

      // Handle mouse click — add new textbox
      c.on("mouse:down", (options) => {
        if (this.tool !== "textbox") return;
        if (c.getActiveObject() instanceof fabric.Textbox) return;

        const pointer = c.getPointer(options.e);
        const textbox = new fabric.Textbox("Add Text...", {
          selectable: true,
          originX: "left",
          originY: "top",
          left: pointer.x,
          top: pointer.y,
          width: 200,
          fontSize: 20,
          fontFamily: "Arial",
          textAlign: "left",
          lineHeight: 1.2,
          fontWeight: "bold",
          fill: this.pickerColor,
          stroke: "white",
          strokeWidth: 0.3,
        });

        c.add(textbox).setActiveObject(textbox);
        c.renderAll();
        textbox.enterEditing();
        textbox.hiddenTextarea && textbox.hiddenTextarea.focus();
      });

      // Handle Enter key while editing text
      c.on("text:editing:entered", (e) => {
        const textbox = e.target;
        const handleKeyDown = (event) => {
          if (event.key === "Enter") {
            textbox.exitEditing();
            c.renderAll();
            document.removeEventListener("keydown", handleKeyDown);
          }
        };
        document.addEventListener("keydown", handleKeyDown);
      });
    },
    // Pen tool setup
    setupPenTool() {
      const c = this.canvasFabric;
      c.isDrawingMode = true;
      const brush = new fabric.PencilBrush(c);
      brush.width = this.penWidth;
      brush.color = this.pickerColor;
      c.freeDrawingBrush = brush;
    },
    // Marker tool setup
    setupMarkerTool() {
      const c = this.canvasFabric;
      c.isDrawingMode = true;
      const brush = new fabric.PencilBrush(c);
      brush.width = 20;
      brush.color = this.pickerColor + "54";
      c.freeDrawingBrush = brush;
    },
    // Eraser tool setup
    setupEraserTool() {
      const c = this.canvasFabric;
      c.isDrawingMode = true;
      c.freeDrawingBrush = new fabric.EraserBrush(c);
      c.freeDrawingBrush.width = 20;
    },
    // Shape tool setup
    setupShapeTool() {
      const c = this.canvasFabric;
      c.isDrawingMode = false;
      c.selection = false;
      c.defaultCursor = "crosshair";
      c.hoverCursor = "pointer";

      let startX,
        startY,
        isDrawing = false;

      c.on("mouse:down", (opt) => {
        if (opt.target && opt.target.type !== "activeSelection") return;
        if (!this.tool) return;

        const pointer = c.getPointer(opt.e);
        startX = pointer.x;
        startY = pointer.y;
        isDrawing = true;

        this.initShapeDrawing(pointer, startX, startY);
      });
      c.on("mouse:move", (opt) => {
        if (!isDrawing || !this.shapeRef) return;
        this.updateShapeDrawing(opt, startX, startY);
      });

      c.on("mouse:up", () => {
        this.finishShapeDrawing();
        isDrawing = false;
      });
    },
    // Initialize shape drawing
    initShapeDrawing(pointer, startX, startY) {
      const fabric = window.fabric;
      const c = this.canvasFabric;

      if (["arrow", "doubleArrow"].includes(this.tool)) {
        this.shapeRef = new fabric.Line([startX, startY, startX, startY], {
          stroke: this.pickerColor,
          strokeWidth: 2,
          selectable: false,
        });

        this.arrowHeadEndRef = new fabric.Triangle({
          width: 15,
          height: 15,
          fill: this.pickerColor,
          left: startX,
          top: startY,
          originX: "center",
          originY: "center",
          angle: 90,
          selectable: false,
        });

        if (this.tool === "doubleArrow") {
          this.arrowHeadStartRef = new fabric.Triangle({
            width: 15,
            height: 15,
            fill: this.pickerColor,
            left: startX,
            top: startY,
            originX: "center",
            originY: "center",
            angle: -90,
            selectable: false,
          });
          c.add(this.shapeRef, this.arrowHeadStartRef, this.arrowHeadEndRef);
        } else {
          c.add(this.shapeRef, this.arrowHeadEndRef);
        }
      } else {
        this.shapeRef = this.createShape(this.tool, pointer);
        if (this.shapeRef) c.add(this.shapeRef);
      }
    },
    // Create shape based on type
    updateShapeDrawing(opt, startX, startY) {
      const c = this.canvasFabric;
      const pointer = c.getPointer(opt.e);
      const shapeRef = this.shapeRef;

      const dx = pointer.x - startX;
      const dy = pointer.y - startY;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Arrow types
      if (["arrow", "doubleArrow"].includes(this.tool)) {
        shapeRef.set({ x1: startX, y1: startY, x2: pointer.x, y2: pointer.y });
        this.arrowHeadEndRef.set({ left: pointer.x, top: pointer.y, angle: angle + 90 });
        if (this.tool === "doubleArrow" && this.arrowHeadStartRef)
          this.arrowHeadStartRef.set({ left: startX, top: startY, angle: angle - 90 });
        c.requestRenderAll();
        return;
      }

      // Other shapes
      const w = pointer.x - startX;
      const h = pointer.y - startY;
      const type = shapeRef.customType || shapeRef.type;

      switch (type) {
        case "rect":
        case "triangle":
          shapeRef.set({
            width: Math.abs(w),
            height: Math.abs(h),
            left: w < 0 ? pointer.x : startX,
            top: h < 0 ? pointer.y : startY,
          });
          break;
        case "circle": {
          const radius = Math.sqrt(w * w + h * h) / 2;
          shapeRef.set({ radius, left: startX - radius, top: startY - radius });
          break;
        }
        case "ellipse":
          shapeRef.set({
            rx: Math.abs(w) / 2,
            ry: Math.abs(h) / 2,
            left: startX - Math.abs(w) / 2,
            top: startY - Math.abs(h) / 2,
          });
          break;
        case "line":
          shapeRef.set({ x2: pointer.x, y2: pointer.y });
          break;
      }

      shapeRef.setCoords();
      c.renderAll();
    },

    // Finalize shape drawing
    finishShapeDrawing() {
      if (this.shapeRef) this.addDeleteControl(this.shapeRef);

      if (["arrow", "doubleArrow"].includes(this.tool)) {
        this.shapeRef.set({ selectable: true });
        if (this.arrowHeadStartRef) this.arrowHeadStartRef.set({ selectable: true });
        if (this.arrowHeadEndRef) this.arrowHeadEndRef.set({ selectable: true });
      }

      //  SAVE UNDO STATE HERE
      this.saveCanvasState();

      this.shapeRef = null;
      this.arrowHeadStartRef = null;
      this.arrowHeadEndRef = null;
      this.drawSavePending = true;
    },

    // Save current canvas state to undo stack
    saveCanvasState() {
      if (!this.canvasFabric) return;
      let json = this.canvasFabric?.toJSON();
      this.undoStack.push(json);
      if (this.undoStack.length > 20) this.undoStack.shift();
      this.redoStack = [];
    },

    // Undo the last action
    undo() {
      if (this.undoStack.length <= 1) return;
      const current = this.undoStack.pop();
      this.redoStack.push(current);
      const prev = this.undoStack[this.undoStack.length - 1];
      this.canvasFabric.loadFromJSON(prev, () => {
        this.canvasFabric.renderAll();
      });
    },

    // Redo the last undone action
    redo() {
      if (this.redoStack.length === 0) return;
      const state = this.redoStack.pop();
      this.undoStack.push(state);
      this.canvasFabric.loadFromJSON(state, () => {
        this.canvasFabric.renderAll();
      });
    },

    //  Shape selection from menu
    selectShapeTool(shapeType) {
      this.tool = shapeType;
      this.showShapesMenu = false;
    },

    //  Check if the current tool is a shape tool
    isShapeToolActive() {
      return this.shapeOptions.find((ele) => ele.value === this.tool);
    },
    toggleMultiSelect() {
      this.showCheckboxes = !this.showCheckboxes;
      if (!this.showCheckboxes) this.selectedImages = [];
    },
    // Confirm before deleting image(s)
    async deleteIssueImageConfirm() {
      if (this.images.length < 2) {
        this.$snackbar.showMessage({
          content: this.$t("Atleast one image should remain"),
          color: "warning",
          timeout: 3000,
        });
        return;
      }
      if (await this.$refs.confirm.open("Confirm", this.$t("c_confirm.t_confirm_delete"))) {
        this.deleteIssueImage();
      }
    },

    // Delete selected image(s)
    async deleteIssueImage() {
      try {
        let selectedImageIds = this.issue.issueImages
          .filter((img) => this.selectedImages.includes(img.imageAssetId))
          .map((img) => img.issueImageId);
        if (selectedImageIds.length === 0) {
          selectedImageIds = [
            this.issue.issueImages.find((img) => img.imageAssetId === this.selectedImage.id)?.issueImageId || null,
          ];
        }

        const params = {
          issueImageIds: selectedImageIds,
        };

        await IssueService.deleteIssueImage(params);
        if (this.selectedImages.length) {
          this.images = this.images.filter((img) => !this.selectedImages.includes(img.id));
        } else {
          this.images = this.images.filter((img) => img.id !== this.selectedImage.id);
        }

        this.selectedImages = [];
        this.showCheckboxes = false;

        this.$snackbar.showMessage({
          content: "Log-it Image " + this.$t("t_deleted"),
          color: "success",
          timeout: "",
        });

        this.selectedImage = this.images[0];
        this.currentImageIndex = 0;
      } catch (error) {
        this.$snackbar.showMessage({
          content: "Failed to delete image.",
          color: "error",
        });
      }
    },

    //  Add delete control to shapes
    addDeleteControl(object) {
      object.controls.deleteControl = new fabric.Control({
        x: 0.5,
        y: -0.5,
        offsetX: 8,
        offsetY: -8,
        cursorStyle: "pointer",
        mouseUpHandler: (e, transform) => {
          const target = transform.target;
          const canvas = target && target.canvas ? target.canvas : this.canvasFabric;
          if (!canvas || !target) return;

          canvas.remove(target);
          canvas.requestRenderAll();
        },
        render: function (ctx, left, top) {
          const size = 16;
          ctx.save();
          ctx.fillStyle = "red";
          ctx.beginPath();
          ctx.arc(left, top, size / 2, 0, 2 * Math.PI);
          ctx.fill();
          ctx.strokeStyle = "white";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(left - 5, top - 5);
          ctx.lineTo(left + 5, top + 5);
          ctx.moveTo(left + 5, top - 5);
          ctx.lineTo(left - 5, top + 5);
          ctx.stroke();
          ctx.restore();
        },
        cornerSize: 16,
      });
    },

    allowTextSelectionOnly() {
      this.canvasFabric.forEachObject(function (object) {
        object.selectable = object.type === "textbox";
      });
      this.canvasFabric.renderAll();
    },

    async submitComment(comment, snack = false) {
      // eslint-disable-next-line no-unused-vars
      const { index, showDetails, ...params } = comment;

      if (!comment.comment) {
        this.$snackbar.showMessage({
          content: this.$t("t_the_comment_is_empty"),
          color: "warning",
          timeout: "",
        });
        this.done();
        return;
      }

      let r;
      if (comment.id) {
        r = await IssueService.patchIssueComment(params);
      } else {
        const matched = this.issue.issueImages?.find((img) => img.imageAssetId === this.selectedImage.id);
        const issueImageId = matched ? matched.issueImageId : null;
        params.issueImageId = issueImageId;
        r = await IssueService.addIssueComment(params);
      }
      this.$set(this.comments[index], "id", r.data?.issueCommentId);
      this.hideComment(index);

      if (snack) {
        this.$snackbar.showMessage({
          content: this.$t("t_comment_saved"),
          color: "success",
          timeout: "",
        });
      }
    },

    disableEraserMode() {
      this.canvasFabric.isDrawingMode = true;
      this.canvasFabric.freeDrawingBrush.color = this.pickerColor;
      this.canvasFabric.freeDrawingBrush.width = 5;
    },

    commentColor(severity) {
      if (severity < 2) return "#FFAB00";
      if (severity < 3) return "#E65100";
      if (severity < 4) return "#B71C1C";
      return "red";
    },

    toggleComment(index) {
      if (this.comments[index].showDetails && !this.isDragging) {
        if (!this.comments[index].id) {
          this.submitComment(this.comments[index], true);
        }
        this.hideComment(index);
      } else {
        // Check if comment should show above based on its position
        const showAbove = this.shouldShowCommentAbove(this.comments[index].yCoord);
        this.$set(this.comments[index], "showAbove", showAbove);
        this.$set(this.comments[index], "showDetails", true);
      }
    },

    hideComment(index) {
      if (!this.comments[index].comment) {
        this.comments.splice(index, 1);
        return;
      }
      this.$set(this.comments[index], "showDetails", false);
      // Reset the current comment if it's the one being hidden
      if (this.currentComment?.index === index) {
        this.currentComment = null;
      }
    },

    triggerComment(event, comment) {
      if (this.isNotOwner(comment)) return;
      event.stopPropagation();
      this.currentComment = comment;
      this.offset.xCoord = this.currentComment.xCoord;
      this.offset.yCoord = this.currentComment.yCoord;
      document.addEventListener("mousemove", this.onCommentDrag);
      document.addEventListener("mouseup", this.endCommentDrag);
      document.addEventListener("touchmove", this.onCommentDrag);
      document.addEventListener("touchend", this.endCommentDrag);
    },

    onCommentDrag(event) {
      if (!this.currentComment) return;
      this.isDragging = true;
      const { xPercent, yPercent } = this.getCoords(event);
      if (xPercent < 1 || xPercent > 99 || yPercent < 1 || yPercent > 99) return;

      const comment = document.getElementById("editComment_" + this.currentComment.index);

      if (xPercent > 70) {
        comment?.classList.toggle("comment-left", true);
      } else {
        comment?.classList.toggle("comment-left", false);
      }

      const showAbove = this.shouldShowCommentAbove(yPercent);

      this.currentComment.xCoord = xPercent;
      this.currentComment.yCoord = yPercent;
      this.currentComment.showAbove = showAbove;

      const commentIndex = this.currentComment.index;
      this.$set(this.comments[commentIndex], "showAbove", showAbove);

      comment?.classList.toggle("comment-above", showAbove);
    },

    endCommentDrag() {
      this.cleanDragListeners();
      if (!this.isDragging) {
        return;
      }
      const idx = this.currentComment.index;
      const comment = this.comments[idx];
      this.$set(this.comments[idx], "xCoord", this.currentComment.xCoord);
      this.$set(this.comments[idx], "yCoord", this.currentComment.yCoord);
      this.currentComment = null;
      this.offset = { x: 0, y: 0 };
      setTimeout(() => {
        this.isDragging = false;
        if (comment.id) {
          IssueService.patchIssueComment(this.comments[idx]);
        }
      }, 100);
    },
    // Clean up event listeners
    cleanDragListeners() {
      document.removeEventListener("mousemove", this.onCommentDrag);
      document.removeEventListener("mouseup", this.endCommentDrag);
      document.removeEventListener("touchmove", this.onCommentDrag);
      document.removeEventListener("touchend", this.endCommentDrag);
    },

    getCoords(event) {
      const canvas = this.$refs.filteredComments;
      const rect = canvas.getBoundingClientRect();
      let x, y;

      if (event.touches && event.touches.length > 0) {
        x = event.touches[0].clientX - rect.left;
        y = event.touches[0].clientY - rect.top;
      } else {
        x = event.clientX - rect.left;
        y = event.clientY - rect.top;
      }
      return {
        xPercent: (x / rect.width) * 100,
        yPercent: (y / rect.height) * 100,
      };
    },

    shouldShowCommentAbove(yPercent) {
      const viewportHeight = window.innerHeight;
      const commentBoxHeight = 300;
      const canvasRect = this.$refs.filteredComments.getBoundingClientRect();
      const triggerY = (yPercent / 100) * canvasRect.height + canvasRect.top;

      // If the space below the trigger is less than the comment box height
      return viewportHeight - triggerY < commentBoxHeight;
    },

    addComment(event) {
      if (this.mode !== "comment") return;
      const { xPercent, yPercent } = this.getCoords(event);
      const showAbove = this.shouldShowCommentAbove(yPercent);

      // Adjust yPercent if showing above to account for the trigger height
      const adjustedYPercent = showAbove ? yPercent - 5 : yPercent;

      this.comments.push({
        issueId: this.issueId,
        issueImageId: this.selectedImage
          ? this.issue.issueImages?.find((img) => img.imageAssetId === this.selectedImage.id)?.issueImageId
          : null,
        index: this.comments.length,
        xCoord: xPercent,
        yCoord: adjustedYPercent,
        comment: "",
        severity: 2,
        showDetails: true,
        showAbove: showAbove,
      });
    },

    async removeCommentConfirm(index, event) {
      if (await this.$refs.confirm.open("Confirm", this.$t("c_confirm.t_confirm_delete"))) {
        this.removeComment(index, event);
      }
    },

    async removeComment(index, event) {
      if (event) event.stopPropagation();
      const comment = this.comments[index];
      if (comment?.id) {
        await IssueService.deleteIssueComment(this.comments[index]?.id);
      }
      this.$snackbar.showMessage({
        content: "Comment deleted",
        color: "success",
        timeout: "",
      });
      this.comments.splice(index, 1);
    },
    // Load images from prop onmount and add issue with images uploaded
    async loadImages() {
      const canvasWrap = this.$refs.canvasWrap;
      this.canvasWidth = canvasWrap.offsetWidth;
      let existingOrders = this.issueImages?.map((img) => img.displayOrder) || [];
      let maxOrder = existingOrders.length ? Math.max(...existingOrders) : 0;
      let imageProps = [];
      for (const photo of this.imagePhotoProp) {
        const imageSrc = `data:image/jpeg;base64,${photo?.base64String}`;
        const imageProp = await this.imageUpload(photo);
        imageProp.displayOrder = ++maxOrder;
        imageProps.push(imageProp);
        const params = {
          id: imageProp.imageAssetId,
          imageSrc: imageSrc,
        };
        this.images.push(params);
        if (this.images.length === 1) {
          this.selectedImage = params;
          this.currentImageIndex = 0;
        }
      }
      this.setImageOnCanvas(this.selectedImage.imageSrc);
      const params = {
        description: "",
        status: "n",
        issueImages: imageProps,
      };
      await this.addIssue(params);
    },
    // Update image on thumbnail click based on screen size
    updateImage(img, idx) {
      this.currentImageIndex = idx;
      this.selectedImage = img;
      this.setImageOnCanvas(this.selectedImage.imageSrc);
    },

    //Set the image dimensions based on the image aspect ratio and container width
    setImageOnCanvas(imageSrc) {
      const img = new Image();
      img.src = imageSrc;

      img.onload = () => {
        const canvasWrap = this.$refs.canvasWrap;
        this.canvasWidth = canvasWrap.offsetWidth;
        this.aspectRatio = img.naturalWidth / img.naturalHeight;
        this.canvasHeight = this.canvasWidth / this.aspectRatio;
        this.originalWidth = this.canvasWidth;
        this.originalHeight = this.canvasHeight;

        this.initFabric();
      };

      const resizeListener = () => {
        const canvasWrap = this.$refs.canvasWrap;
        this.canvasWidth = canvasWrap.offsetWidth;
        this.aspectRatio = img.naturalWidth / img.naturalHeight;
        this.canvasHeight = this.canvasWidth / this.aspectRatio;
        this.resizeCanvas();
      };

      window.addEventListener("resize", resizeListener);
      this.$once("hook:destroyed", () => {
        window.removeEventListener("resize", resizeListener);
      });
    },
    async addIssue(params) {
      if (!params) {
        if (!this.issue) return;
        params = {
          description: this.issue.description,
          status: this.issue.status,
          issueImages: params.issueImages,
        };
      }
      const r = await IssueService.addIssue(params);
      this.$router.push({ name: "issue", params: { id: r.data.issueId } });
      this.issueId = r.data?.issueId;
      const response = await IssueService.getIssue(this.issueId);
      this.issue = response.data;
      this.$snackbar.showMessage({
        content: "Log-it " + this.$t("t_photo_uploaded"),
        color: "success",
        timeout: "",
      });
    },
    // Add images using Capacitor Camera API
    async addImages() {
      try {
        const options = {
          quality: 90,
          limit: 10,
        };
        const photos = await Camera.pickImages(options);
        const processedImages = [];
        const imageProps = [];
        const existingOrders = this.issue?.issueImages?.map((img) => img.displayOrder) || [];
        let maxDisplayOrder = existingOrders.length ? Math.max(...existingOrders) : 0;
        for (const photo of photos.photos) {
          try {
            const response = await fetch(photo.webPath);
            const blob = await response.blob();
            if (!blob.type.startsWith("image/")) {
              this.$snackbar.showMessage({
                content: this.$t("Invalid file type, Please select an image."),
                color: "error",
              });
              continue;
            }

            // Convert blob -> base64 string
            const base64String = await this.blobToBase64(blob);

            // Build image object
            let image = {
              ...photo,
              base64String: base64String.replace(/^data:image\/[a-z]+;base64,/, ""), // strip prefix
            };

            // Convert HEIC or problematic files
            if (
              (image.base64String && image.base64String.startsWith("AAAA")) ||
              (image.format && image.format.toLowerCase() === "heic")
            ) {
              image = await this.convertHeicToJpeg(image);
            }

            let imageProp = await this.imageUpload(image, true);
            imageProp.displayOrder = ++maxDisplayOrder;
            imageProps.push(imageProp);

            // Add to processed list
            processedImages.push(image);

            // Build UI display params
            const imageSrc = `data:image/jpeg;base64,${image?.base64String}`;
            const params = {
              id: imageProp.imageAssetId,
              imageSrc: imageSrc,
            };
            this.images.push(params);
          } catch (err) {
            console.error("Error processing image:", err);
          }
        }

        // Update issue with new images
        if (imageProps.length === 0) return;
        const params = {
          id: this.issueId,
          description: this.issue.description,
          status: this.issue.status,
          issueImages: imageProps,
        };

        await IssueService.patchIssue(params);
        const response = await IssueService.getIssue(this.issueId);
        this.issue = response.data;
      } catch (error) {
        console.error("Error processing image:", error);
      }
    },

    // Convert HEIC images to JPEG using heic2any
    async convertHeicToJpeg(image) {
      try {
        const blob = this.base64ToBlob(image.base64String, "image/heic");
        const jpegBlob = await heic2any({
          blob: blob,
          toType: "image/jpeg",
          quality: 0.9,
        });

        const jpegBase64 = await this.blobImageToBase64(jpegBlob);

        return {
          base64String: jpegBase64,
          format: "jpeg",
          ...Object.keys(image)
            .filter((key) => !["base64String", "format"].includes(key))
            .reduce((obj, key) => ({ ...obj, [key]: image[key] }), {}),
        };
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        throw error;
      }
    },
    // Convert base64 string to Blob
    base64ToBlob(base64, type) {
      const byteString = atob(base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type });
    },
    // Convert Blob image to base64 string
    blobImageToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result;
          resolve(base64.split(",")[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    //Upload image to server, handle both existing imagePhotoProp and new file uploads
    async imageUpload(file = null, upload = false) {
      // Handle existing imagePhotoProp case
      if (this.imagePhotoProp || upload) {
        this.pending();
        const base64String = file.base64String;
        const base64Data = new Buffer.from(base64String, "base64");
        const photoExt = "jpg";
        const photoName = `log-it-photo_${new Date().getTime()}.${photoExt}`;

        const payload = {
          name: photoName,
          description: "",
          originalFileName: photoName,
          mimeType: "image/jpeg",
          cacheable: true,
          permitCompany: true,
          includeThumbnail: true,
        };

        try {
          const response = await AssetService.uploadAssetBegin(payload);

          // Process thumbnail
          const thumbnailBase64 = await this.processImage(base64String, {
            maxDimension: 600,
            quality: 0.7,
          });

          // Convert thumbnail to Buffer
          const thumbnailBuffer = new Buffer.from(thumbnailBase64, "base64");

          // Upload original image
          await fetch(response.data.uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.format,
              "Content-Encoding": "base64",
            },
            body: base64Data,
          });

          // Upload thumbnail
          await fetch(response.data.thumbnailUploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.format,
              "Content-Encoding": "base64",
            },
            body: thumbnailBuffer,
          });

          await AssetService.uploadAssetComplete({
            assetId: response.data.assetId,
            hasThumbnail: true,
          });

          const params = {
            imageAssetId: response.data.assetId,
            imageAssetPublicId: response.data.assetGuid,
          };
          return params;
        } catch (error) {
          console.error("Error uploading image:", error);
          throw error;
        } finally {
          this.done();
        }
      }

      // Handle new file upload case
      try {
        const formData = new FormData();
        formData.append("file", file);

        // Get the upload URLs
        const response = await AssetService.UploadAssetBegin({
          issueId: this.issueId,
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          includeThumbnail: true,
        });

        // Get base64 string from file for thumbnail processing
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result.split(",")[1]);
          reader.readAsDataURL(file);
        });
        const base64String = await base64Promise;

        // Process thumbnail image
        const thumbnailBase64 = await this.processImage(base64String, {
          maxDimension: 600,
          quality: 0.7,
        });

        // Convert thumbnail base64 back to Blob
        const thumbnailBlob = await fetch(`data:${file.type};base64,${thumbnailBase64}`).then((res) => res.blob());

        // Upload original image
        await fetch(response.data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
        });

        // Upload thumbnail image
        await fetch(response.data.thumbnailUploadUrl, {
          method: "PUT",
          body: thumbnailBlob,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
        });

        // Complete the upload
        await AssetService.UploadAssetComplete({
          issueId: this.issueId,
          assetId: response.data.assetId,
          hasThumbnail: true,
        });

        return response.data.assetId;
      } catch (error) {
        console.error("Error uploading image:", error);
        throw error;
      }
    },

    async saveMarkup() {
      try {
        const selectedImageId = this.selectedImage?.id;
        const issueImage = this.issue?.issueImages?.find((img) => img.imageAssetId === selectedImageId);

        await this.saveCanvasAsJSON();
        this.saveCombinedCanvasImage(issueImage.imageAssetPublicId);
      } catch (err) {
        console.error("saveMarkup failed:", err);

        this.$snackbar.showMessage({
          content: this.$t("t_save_failed") || "Save failed",
          color: "error",
          timeout: 6000,
        });
      } finally {
        this.drawSavePending = false;
        this.done();
      }
    },
    async saveCombinedCanvasImage(publicId) {
      if (this.canvasFabric) {
        this.canvasFabric.setWidth(this.canvasWidth);
        this.canvasFabric.setHeight(this.canvasHeight);
      }

      const combinedBlob = await this.combineBaseAndMarkupToBlob("image/png", 0.92);

      const beginRes = await AssetService.beginCombinedCanvasUpload({ publicAssetId: publicId });
      const thumbBlob = await this.scaleImageBlob(combinedBlob, 600, 0.7);

      const uploadUrl = beginRes?.data?.canvasUploadUrl;
      const thumbnailUploadUrl = beginRes?.data?.canvasThumbUploadUrl;

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": combinedBlob.type },
        body: combinedBlob,
      });
      // Upload thumbnail
      await fetch(thumbnailUploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": combinedBlob.type,
        },
        body: thumbBlob,
      });
    },

    async combineBaseAndMarkupToBlob(outputType = "image/png", quality = 0.92) {
      const baseImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = (e) => reject(new Error("Failed to load base image: " + e));
        img.src = this.selectedImage?.imageSrc;
      });

      const naturalW = baseImg.naturalWidth || this.originalWidth || this.canvasWidth;
      const naturalH = baseImg.naturalHeight || this.originalHeight || this.canvasHeight;

      const displayedWidth = this.canvasFabric.getWidth() || this.canvasWidth;
      const multiplier = naturalW / displayedWidth || 1;

      const overlayDataUrl = this.canvasFabric.toDataURL({
        format: "png",
        multiplier: multiplier,
        left: 0,
        top: 0,
        width: this.canvasFabric.getWidth(),
        height: this.canvasFabric.getHeight(),
        enableRetinaScaling: false,
      });

      const off = document.createElement("canvas");
      off.width = Math.round(naturalW);
      off.height = Math.round(naturalH);
      const ctx = off.getContext("2d");

      ctx.drawImage(baseImg, 0, 0, off.width, off.height);

      const overlayImg = await new Promise((resolve, reject) => {
        const oi = new Image();
        oi.crossOrigin = "Anonymous";
        oi.onload = () => resolve(oi);
        oi.onerror = (e) => reject(new Error("Failed to load overlay image: " + e));
        oi.src = overlayDataUrl;
      });

      ctx.drawImage(overlayImg, 0, 0, off.width, off.height);

      const blob = await new Promise((resolve) => {
        if (off.toBlob) {
          off.toBlob((b) => resolve(b), outputType, quality);
        } else {
          const dataUrl = off.toDataURL(outputType, quality);
          const arr = dataUrl.split(",");
          const mime = arr[0].match(/:(.*?);/)[1];
          const binStr = atob(arr[1]);
          let n = binStr.length;
          const u8arr = new Uint8Array(n);
          while (n--) u8arr[n] = binStr.charCodeAt(n);
          resolve(new Blob([u8arr], { type: mime }));
        }
      });

      return blob;
    },

    async saveCanvasAsJSON() {
      this.pending();
      const selectedImageId = this.selectedImage?.id; // The selected image's assetId
      if (!selectedImageId) {
        this.done();
        return;
      }

      // Find the matching issueImage
      const issueImage = this.issue?.issueImages?.find((img) => img.imageAssetId === selectedImageId);
      if (!issueImage) {
        this.done();
        return;
      }

      const existingMarkupAssetId = issueImage.markupAssetId;

      this.canvasFabric.width = this.canvasWidth;
      this.canvasFabric.height = this.canvasHeight;
      const jsonCanvas = this.canvasFabric.toJSON(["width", "height"]);
      const jsonCanvasString = JSON.stringify(jsonCanvas);
      const photoName = `log-it-markup_${this.issueId}.json`;
      const payload = {
        name: photoName,
        description: "",
        originalFileName: photoName,
        mimeType: "application/json",
        cacheable: true,
        permitCompany: true,
      };

      try {
        const a = await AssetService.uploadAssetBegin(payload);
        const uploadUrl = a.data.uploadUrl;
        const assetGuid = a.data.assetGuid;
        const markupAssetId = a.data.assetId;

        const r = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: jsonCanvasString,
        });

        if (r.ok) {
          await AssetService.uploadAssetComplete({ assetId: markupAssetId });

          issueImage.markupAssetId = markupAssetId;
          issueImage.markupAssetPublicId = assetGuid;

          const params = {
            id: this.issueId,
            issueImages: this.issue.issueImages,
          };

          const res = await IssueService.patchIssue(params);

          if (existingMarkupAssetId && !res.hasErrors) {
            await AssetService.deleteAsset(existingMarkupAssetId, true);
          }
        } else {
          await AssetService.deleteAsset(markupAssetId, true);
        }
      } catch (error) {
        console.error("An error occurred during the asset upload process:", error);
      } finally {
        this.done();
      }
      setTimeout(() => {
        this.done();
      }, 8000);
    },

    async submitIssue() {
      if (this.drawSavePending) {
        const selectedImageId = this.selectedImage?.id;

        const issueImage = this.issue?.issueImages?.find((img) => img.imageAssetId === selectedImageId);

        await this.saveCanvasAsJSON();
        await this.saveCombinedCanvasImage(issueImage.imageAssetPublicId);
      }

      const unsavedComments = this.comments.filter((obj) => obj.id === undefined);
      unsavedComments.forEach((obj) => this.submitComment(obj, false));

      this.$router.push("/issues");
      this.$snackbar.showMessage({
        content: "Log-it " + this.$t("t_saved"),
        color: "success",
        timeout: "",
      });
    },

    async updateIssue() {
      if (this.issueId && this.issue.description.length > 0) {
        const params = { id: this.issueId, description: this.issue.description, status: this.issue.status };
        await IssueService.patchIssue(params);
        this.$snackbar.showMessage({
          content: "Log-it " + this.$t("t_saved"),
          color: "success",
          timeout: "",
        });
      }
    },

    async deleteIssueConfirm() {
      if (await this.$refs.confirm.open("Confirm", this.$t("c_confirm.t_confirm_delete"))) {
        this.deleteIssue();
      }
    },

    async deleteIssue() {
      const params = { id: this.issueId, status: "d" };
      await IssueService.patchIssue(params);
      this.$snackbar.showMessage({
        content: "Log-it " + this.$t("t_deleted"),
        color: "success",
        timeout: "",
      });
      this.$router.push("/issues");
    },

    ///////// Load Issue ///////////////

    async loadIssue() {
      this.pending();
      this.issue = this.issueProp;
      // Normalize legacy "n" status to "o" (Open)
      if (this.issue.status === "n") {
        this.issue.status = "o";
      }
      this.issueId = this.issue.id;
      this.issue = this.normalizeIssueTags(this.issue);

      await this.getPhotoBase64();
      if (this.selectedImage) {
        await this.getCanvasJSON();
      }
      this.getIssueComments();
      this.done();
    },
    normalizeIssueTags(issue) {
      if (!issue.tags) {
        issue.tags = [];
        return issue;
      }

      if (Array.isArray(issue.tags)) {
        issue.tags = issue.tags
          .map((t) => {
            if (typeof t === "object") return t;
            return this.tagsList.find((tag) => tag.id === t || tag.name === t);
          })
          .filter(Boolean);
      } else if (typeof issue.tags === "string") {
        const tagNames = issue.tags.split(",").map((x) => x.trim());
        issue.tags = tagNames.map((name) => this.tagsList.find((tag) => tag.name === name)).filter(Boolean);
      }

      return issue;
    },

    async getIssueComments() {
      if (this.comments?.length) {
        this.comments = [];
      }
      let params = {
        issueId: this.issueId,
        "paging.orderbyfield": "id",
        "paging.ascending": true,
        "paging.skipnum": 0,
        "paging.takenum": 999999,
      };

      const r = await IssueService.getIssueComments(params);
      const comments = r.data;
      comments.forEach((c, idx) => {
        c.index = idx;
        this.comments.push(c);
      });
    },

    async getCanvasJSON() {
      if (!this.selectedImage) return;

      const issueImage = this.issue?.issueImages?.find((img) => img.imageAssetId === this.selectedImage.id);

      if (!issueImage || !issueImage.markupAssetId) {
        this.canvasFabric?.clear();
        this.setImageOnCanvas(this.selectedImage.imageSrc); // fallback to base image
        return;
      }
      let params = {
        assetId: issueImage.markupAssetId,
        token: issueImage.markupAssetPublicId,
      };
      const r = await AssetService.downloadAsset(params);
      if (!r?.data?.downloadUrl) return;

      const data = await fetch(r.data.downloadUrl);
      const json = await data.json();
      await this.loadCanvasFromJSON(json);
    },
    // Load canvas from JSON and scale objects based on new canvas size
    loadCanvasFromJSON(json) {
      if (!this.canvasFabric || !json) {
        return;
      }
      const originalWidth = json?.width;
      const originalHeight = json?.height;
      const newWidth = this.canvasFabric?.width;
      const newHeight = this.canvasFabric?.height;
      const scaleX = newWidth / originalWidth;
      const scaleY = newHeight / originalHeight;

      this.canvasFabric.loadFromJSON(json, () => {
        this.canvasFabric.forEachObject((obj) => {
          obj.scaleX *= scaleX;
          obj.scaleY *= scaleY;
          obj.left *= scaleX;
          obj.top *= scaleY;
          obj.setCoords();
        });
        this.canvasFabric.renderAll();
      });
    },
    async getPhotoBase64() {
      if (!this.issue?.issueImages?.length) return;
      for (const image of this.issue.issueImages) {
        let params = { assetId: image.imageAssetId, token: image.imageAssetPublicId };
        const r = await AssetService.downloadAsset(params);
        if (r?.data?.downloadUrl == null) continue;

        const d = await fetch(r.data.downloadUrl);
        const imageBlob = await d.blob();
        const base64 = await this.blobToBase64(imageBlob);
        const canvasWrap = this.$refs.canvasWrap;
        this.canvasWidth = canvasWrap?.offsetWidth;
        const base64String = base64.split(",")[1];

        const imageSrc = `data:image/jpeg;base64,${base64String}`;
        const imageParams = {
          id: image.imageAssetId,
          imageSrc: imageSrc,
        };
        this.images.push(imageParams);

        if (this.images.length === 1) {
          this.selectedImage = imageParams;
          this.currentImageIndex = 0;
        }
      }
      this.setImageOnCanvas(this.selectedImage.imageSrc);
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          const base64data = reader.result;
          resolve(base64data);
        };
        reader.onerror = function (error) {
          reject(error);
        };
      });
    },

    isNotOwner(comment) {
      return comment.ownerId && comment.ownerId != this.user?.userId;
    },

    handleKeydown(e) {
      if ((e.key === "Delete" || e.keyCode === 46) && !e.target.tagName.match(/INPUT|TEXTAREA|SELECT/)) {
        let activeObject = this.canvasFabric.getActiveObject();
        // Check if the active object is a textbox and it's not in editing mode
        if (activeObject && activeObject.type === "textbox" && !activeObject.isEditing) {
          this.canvasFabric.remove(activeObject);
          this.canvasFabric.requestRenderAll();
        }
      }
    },

    resizeCanvas() {
      const newWidth = this.canvasWidth;
      const newHeight = this.canvasHeight;

      const scaleX = newWidth / this.originalWidth;
      const scaleY = newHeight / this.originalHeight;

      this.canvasFabric.getObjects().forEach((obj) => {
        const newLeft = obj.left * scaleX;
        const newTop = obj.top * scaleY;

        obj.set({
          left: newLeft,
          top: newTop,
          scaleX: obj.scaleX * scaleX,
          scaleY: obj.scaleY * scaleY,
        });

        obj.setCoords();
      });

      this.canvasFabric.setWidth(newWidth);
      this.canvasFabric.setHeight(newHeight);
      this.canvasFabric.renderAll();
      this.originalWidth = newWidth;
      this.originalHeight = newHeight;
    },
    //initialize fabric canvas
    initFabric() {
      const fabric = window.fabric;

      if (!this.canvasFabric) {
        this.canvasFabric = new fabric.Canvas(this.$refs.canvasFabric, {
          isDrawingMode: false, // default off, tool decides later
          width: this.canvasWidth,
          height: this.canvasHeight,
          selection: true,
        });

        // Default brush
        const pencilBrush = new fabric.PencilBrush(this.canvasFabric);
        pencilBrush.width = this.penWidth;
        pencilBrush.color = this.pickerColor;
        this.canvasFabric.freeDrawingBrush = pencilBrush;

        // Events
        this.canvasFabric.on("path:created", () => {
          this.saveCanvasState();
          this.drawSavePending = true;
        });
        this.canvasFabric.on("text:editing:exited", (e) => {
          if (e.target && e.target.type === "textbox") {
            this.saveCanvasState();
            this.drawSavePending = true;
          }
        });

        // Keydown delete
        window.addEventListener("keydown", this.handleKeydown);
      } else {
        this.canvasFabric.clear();
        this.canvasFabric.setWidth(this.canvasWidth);
        this.canvasFabric.setHeight(this.canvasHeight);
      }

      // Always reset all event listeners before setting up the current tool
      this.canvasFabric.off("mouse:down");
      this.canvasFabric.off("mouse:move");
      this.canvasFabric.off("mouse:up");

      // Let the active tool set up its behavior (pen, shape, etc.)
      this.setTool();
    },

    // Getting the list of users
    async shareLogit() {
      const r = await UserService.getUsers();
      const currentUserId = Number(this.user?.userId);
      const users = (r.data || []).filter((u) => u.id !== currentUserId);

      for (const user of users) {
        user.imageUrl = await this.generateImageUrl(user);
      }

      this.users = users;
      this.shareDialog = true;
    },

    //  Generating the profile image Url for user
    async generateImageUrl(user) {
      try {
        // No profile image → return null (so initials will be shown)
        if (!user?.profileImageAssetId) {
          return null;
        }

        // Try IndexedDB cache first
        const s3Item = await idb.getS3Item(user.profileImageAssetKey);
        if (s3Item?.value) {
          return URL.createObjectURL(s3Item.value);
        }

        // Build request params
        let params = { assetId: user.profileImageAssetId, token: user.profileImageAssetKey };
        if (user.userId === this.$store.state.user?.user?.userId) {
          // Current logged-in user doesn’t need token
          params = { assetId: user.profileImageAssetId };
        }

        // Fetch download URL from S3
        const r = await AssetService.downloadAsset(params);
        if (!r?.data?.downloadUrl) {
          return null;
        }

        // Download blob and cache in IndexedDB
        const d = await fetch(r.data.downloadUrl);
        const imageBlob = await d.blob();
        await idb.putS3Item(user.profileImageAssetKey, imageBlob);

        return URL.createObjectURL(imageBlob);
      } catch (err) {
        console.error("Error generating image URL", err);
        return null;
      }
    },

    // Calling shareIssue API to send email and notification
    async handleShare(payload) {
      try {
        const shareParams = {
          message: payload.message,
          entityId: this.issueId,
          userIds: payload.users,
          title: this.issue?.description || "Log-it Entry",
          sendEmail: true,
          sendSms: true,
          notificationType: "share",
          notificationSource: "issue",
          notificationSubType: "logit",
        };
        await IssueService.shareIssue(shareParams);
        this.$snackbar.showMessage({
          content: "Log-it shared successfully",
          color: "success",
          timeout: "",
        });
      } catch (error) {
        this.$snackbar.showMessage({
          content: "Error sharing Log-it",
          color: "error",
          timeout: "",
        });
      }
    },
    //  Helper for shape creation
    createShape(type, pointer) {
      const stroke = this.pickerColor;
      const fill = "transparent"; // transparent fill for shapes

      switch (type) {
        case "rectangle":
          return new fabric.Rect({
            left: pointer.x,
            top: pointer.y,
            width: 0,
            height: 0,
            fill,
            stroke,
            strokeWidth: 2,
            selectable: true,
          });

        case "circle":
          return new fabric.Circle({
            left: pointer.x,
            top: pointer.y,
            radius: 0,
            fill,
            stroke,
            strokeWidth: 2,
            selectable: true,
          });

        case "triangle":
          return new fabric.Triangle({
            left: pointer.x,
            top: pointer.y,
            width: 0,
            height: 0,
            fill,
            stroke,
            strokeWidth: 2,
            selectable: true,
          });

        case "ellipse":
          return new fabric.Ellipse({
            left: pointer.x,
            top: pointer.y,
            rx: 0,
            ry: 0,
            fill,
            stroke,
            strokeWidth: 2,
            selectable: true,
          });

        case "line":
          return new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
            stroke,
            strokeWidth: 2,
            selectable: true,
          });

        default:
          return null;
      }
    },
  },
  async mounted() {
    if (this.issueProp) {
      await this.loadIssue();
    } else if (this.imagePhotoProp) {
      await this.loadImages();
    }
    setTimeout(() => {
      this.saveCanvasState(true);
    }, 100);
  },

  beforeDestroy() {
    this.setImage(null);
    document.removeEventListener("mousemove", this.onCommentDrag);
    document.removeEventListener("mouseup", this.endCommentDrag);
    document.removeEventListener("touchmove", this.onCommentDrag);
    document.removeEventListener("touchend", this.endCommentDrag);
  },
};
</script>

<style lang="scss">
.theme--dark .image-editor {
  background: #272727;
  .btn-editor,
  .btn-tool {
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
}
.image-editor {
  background: #eee;
  padding-bottom: 15px;

  img.photo {
    position: absolute;
    top: 0;
    left: 0;
  }
  @media (max-width: 768px) {
    img.photo {
      position: static;
      display: block;
      width: 100%;
      height: auto;
      margin-bottom: 12px;
    }
  }
  .canvas {
    width: 100%;
    height: auto;
  }
  .comment-nav,
  .tools {
    margin-top: -10px;
  }
  .btn-submit {
    border-radius: 3px !important;
    padding: 0px 10px !important;
    height: 33px !important;
  }
  .btn-editor {
    //border: 2px solid #dadada !important;
    border-radius: 3px !important;
    padding: 4px 6px !important;
    height: 34px !important;
    min-width: unset !important;
    &.active {
      background: rgba(255, 255, 255, 0.25);
    }
    border: 1px solid rgba(0, 0, 0, 0.2);
  }
  .btn-tool {
    border-radius: 3px !important;
    //border: 2px solid #4f4f4f;
    padding: 4px 6px !important;
    height: 34px !important;
    min-width: 28px !important;
    //border: 1px solid rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(0, 0, 0, 0.2);
    &.active {
      background: rgba(255, 255, 255, 0.25);
      //border: 2px solid #ccc;
    }
  }
  .canvas-wrap {
    position: relative;
    border-top: 2px solid #aaa;
    z-index: 4;
    margin: 0 15px;

    .canvas.fabric {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      width: 100% !important;
      height: 100% !important;
      z-index: 99;
    }
    .canvas-container {
      position: absolute !important;
      width: 100% !important;
      height: 100% !important;
      //padding-bottom: 100%;
      z-index: 1;
      canvas {
        width: 100% !important;
        height: 100% !important;
      }
    }
    .upper-canvas {
      width: 100% !important;
      height: 100% !important;
    }

    .canvas {
      position: absolute;
      top: 0;
      left: 0;
      &.photo {
        z-index: 10;
      }
      &.draw {
        z-index: 20;
      }
    }
    &.draw {
      cursor: url("~@/assets/pencil.svg") 4 28, auto;
    }
    &.eraser {
      cursor: url("~@/assets/eraser.svg") 4 28, auto;
    }
    &.comment {
      cursor: url("~@/assets/comment-text.svg") 14 28, auto;
    }
    .comments-wrap {
      position: relative;
      .comment-wrap {
        margin: -25px 0px 0px -25px;
        position: absolute;
        z-index: 1;
        &.active {
          .trigger-show-comment .large-circle {
            stroke: rgba(250, 246, 177, 0.4);
            stroke-width: 12;
          }
        }
      }
      .comment-avatar {
        position: relative;
        left: 0px;
        top: -2px;
      }
      .trigger-show-comment {
        cursor: pointer;
        touch-action: none;
        width: 50px;
        height: 50px;
        &.show-move {
          cursor: move;
        }
        // .comment-avatar {
        //   z-index: 20;
        //   position: relative;
        //   border: 2px solid #fff !important;
        //   left: 7px;
        //   top: 7px;
        // }
        .edit-connect {
          position: relative;
          left: 23px;
          top: -15px;
          height: 30px;
          width: 5px;
          background: #fff;
          &.avatar {
            left: 23px;
            top: 0;
          }
          &.comment-above {
            top: -79px;
          }
        }
      }
      .edit-comment {
        cursor: default;
        position: relative;
        background: #f4f4f4;
        padding: 8px 15px 15px;
        border: 5px solid #dedede;
        border-radius: 8px;
        width: 360px;
        .comment-read {
          background: #fff;
        }
        &.dark {
          background: #333;
          border: 5px solid #fff;
          .comment-read {
            background: #262626;
          }
        }
        &.comment-above {
          &.comment-left {
            .edit-connect {
              left: 236px;
            }
          }
        }
        &.comment-left {
          left: -280px;
          .edit-connect {
            left: 236px;
          }
        }
        .textarea textarea {
          line-height: 1.3;
          padding-bottom: 10px;
        }
        // .v-slider__track-background {
        //   background-color: transparent !important;
        // }
        .v-slider__track-fill {
          background-color: #5c5c5c !important;
        }
        .btn-close-comment {
          position: absolute;
          right: -15px;
          top: -15px;
          width: 25px;
          height: 25px;
          background: #dedede;
        }
      }
    }
  }
  .image-editor canvas {
    border: 1px solid #ccc;
  }
  @keyframes severity-level {
    from {
      transform: scale(0.5);
    }

    to {
      transform: scale(1);
    }
  }
}
@media (max-width: 768px) {
  .comments-wrap {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    pointer-events: auto;
    z-index: 30;
  }
}
@media (min-width: 768px) {
  .image-editor {
    .canvas-wrap {
      .comments-wrap {
        .edit-comment {
          position: relative;
          // top: -95px !important;
          // left: 60px !important;
          max-width: 420px !important;

          .edit-connect {
            position: relative;
            border-bottom: 5px solid #fbecec;
            left: -45px;
            top: 55px;
            width: 30px;
          }
          &.comment-left {
            left: -280px;
            .edit-connect {
              left: 236px;
            }
          }
        }
      }
    }
  }
}
.image-manager-text {
  background-color: var(--v-theme-surface-variant);
}
.image-manager.v-card {
  background: #f2f2f2 !important;
}

.theme--dark.image-manager.v-card {
  background: #484646 !important;
}

.image-manager.v-card {
  border-radius: 10px;
  padding: 8px;

  .thumb {
    flex: 0 0 auto;
    width: clamp(75px, 10vw, 90px);
    aspect-ratio: 1;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, outline 0.2s;
    position: relative;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
    }

    &.active {
      outline: 2px solid #1976d2;
    }
  }
  .thumb-number {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border-radius: 3px;
    font-size: 12px;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .add-thumb {
    border: 2px dashed #1976d2;
    color: #1976d2;
    justify-content: center;
    align-items: center;
    transition: background 0.2s;

    &:hover {
      background: rgba(25, 118, 210, 0.1);
    }
  }
  .thumb-checkbox {
    position: absolute !important;
    top: -18px;
    right: -10px;
    z-index: 2;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.4);
    padding: 1px;

    .v-selection-control__input {
      transform: scale(0.6); // reduce size to 60%
      margin: 0; // remove extra spacing
    }

    .v-label {
      font-size: 0 !important; // hide label
      width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }
  }

  .thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-btn {
    min-width: unset;
    font-size: 13px;
  }
}
.scroll-row {
  flex: 1;
  flex-wrap: nowrap !important;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.scroll-row::-webkit-scrollbar {
  height: 6px;
}
.scroll-row::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 4px;
}
</style>
